import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useProfile, useIsAdmin } from "@/lib/data-hooks";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import {
  NOTIF_CATEGORIES,
  channelPrefsFor,
  useNotificationPrefs,
  type NotifPrefs,
} from "@/lib/notification-prefs";

export const Route = createFileRoute("/_authenticated/settings")({
  head: () => ({ meta: [{ title: "Settings — CryptoBull Pro" }] }),
  component: Settings,
});

function Settings() {
  const { data: profile } = useProfile();
  const { data: isAdmin, isLoading: roleLoading, refetch: refetchAdmin } = useIsAdmin();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [addr, setAddr] = useState("");
  const { data: prefsData } = useNotificationPrefs();
  const [prefs, setPrefs] = useState<NotifPrefs>({});
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    if (profile) { setName(profile.full_name ?? ""); setAddr(profile.btc_address ?? ""); }
  }, [profile]);

  useEffect(() => {
    if (prefsData) setPrefs(prefsData);
  }, [prefsData]);

  const normalizedPrefs = useMemo(() => {
    const out: NotifPrefs = {};
    for (const c of NOTIF_CATEGORIES) out[c.key] = channelPrefsFor(prefs, c.key);
    return out;
  }, [prefs]);

  const setChannel = (cat: string, channel: "in_app" | "email", value: boolean) => {
    setPrefs((p) => {
      const current = channelPrefsFor(p, cat as never);
      return { ...p, [cat]: { ...current, [channel]: value } };
    });
  };

  const savePrefs = async () => {
    if (!profile) return;
    setSavingPrefs(true);
    const { error } = await supabase
      .from("notification_preferences")
      .upsert({ user_id: profile.id, prefs: normalizedPrefs }, { onConflict: "user_id" });
    setSavingPrefs(false);
    if (error) toast.error(error.message);
    else {
      toast.success("Notification preferences saved");
      qc.invalidateQueries({ queryKey: ["notification_prefs"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="text-2xl font-semibold">Settings</h1>

      <div className={`glass rounded-2xl p-5 flex items-center justify-between gap-4 border ${isAdmin ? "border-emerald-500/40" : "border-white/10"}`}>
        <div>
          <div className="text-sm font-medium flex items-center gap-2">
            Admin access
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${isAdmin ? "bg-emerald-500/15 text-emerald-400" : "bg-white/10 text-muted-foreground"}`}>
              {roleLoading ? "Checking…" : isAdmin ? "Enabled" : "Not granted"}
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {isAdmin
              ? "You can access the Admin Dashboard at /admin."
              : "Your account does not have the admin role. If you are the first user, you can claim it below."}
          </div>
        </div>
        {!isAdmin && !roleLoading && (
          <Button
            type="button"
            variant="outline"
            onClick={async () => {
              const { error } = await supabase.rpc("claim_first_admin");
              if (error) { toast.error(error.message); return; }
              toast.success("Admin role granted");
              await refetchAdmin();
              qc.invalidateQueries({ queryKey: ["is-admin"] });
            }}
          >
            Claim admin
          </Button>
        )}
      </div>

      <form className="glass rounded-2xl p-6 space-y-4" onSubmit={async (e) => {
        e.preventDefault();
        const { error } = await supabase.from("profiles").update({ full_name: name, btc_address: addr }).eq("id", profile!.id);
        if (error) toast.error(error.message);
        else { toast.success("Profile updated"); qc.invalidateQueries({ queryKey: ["profile"] }); }
      }}>
        <div className="text-sm font-medium">Profile</div>
        <div className="space-y-2"><Label>Full name</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="space-y-2"><Label>Email</Label><Input value={profile?.email ?? ""} disabled /></div>
        <div className="space-y-2"><Label>Default BTC wallet address</Label><Input value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="bc1q…" /></div>
        <Button type="submit">Save</Button>
      </form>

      <form className="glass rounded-2xl p-6 space-y-4" onSubmit={async (e) => {
        e.preventDefault();
        const pw = String(new FormData(e.currentTarget).get("password"));
        const { error } = await supabase.auth.updateUser({ password: pw });
        if (error) toast.error(error.message); else toast.success("Password changed");
        (e.currentTarget as HTMLFormElement).reset();
      }}>
        <div className="text-sm font-medium">Change password</div>
        <div className="space-y-2"><Label>New password</Label><Input name="password" type="password" minLength={8} required /></div>
        <Button type="submit">Update password</Button>
      </form>

      <section className="glass rounded-2xl p-6 space-y-4">
        <div>
          <div className="text-sm font-medium">Notification preferences</div>
          <p className="text-xs text-muted-foreground mt-1">
            Choose which alerts appear in your in-app notification center and which are sent to your email.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-4 font-medium">Category</th>
                <th className="py-2 px-4 font-medium w-20 text-center">In-app</th>
                <th className="py-2 pl-4 font-medium w-20 text-center">Email</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {NOTIF_CATEGORIES.map((c) => {
                const v = channelPrefsFor(prefs, c.key);
                return (
                  <tr key={c.key}>
                    <td className="py-3 pr-4">
                      <div className="font-medium">{c.label}</div>
                      <div className="text-xs text-muted-foreground">{c.description}</div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Switch
                        checked={v.in_app}
                        onCheckedChange={(val) => setChannel(c.key, "in_app", val)}
                        aria-label={`${c.label} in-app`}
                      />
                    </td>
                    <td className="py-3 pl-4 text-center">
                      <Switch
                        checked={v.email}
                        onCheckedChange={(val) => setChannel(c.key, "email", val)}
                        aria-label={`${c.label} email`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] text-muted-foreground">
            Email alerts are sent to {profile?.email ?? "your account email"}.
          </p>
          <Button onClick={savePrefs} disabled={savingPrefs}>
            {savingPrefs ? "Saving…" : "Save preferences"}
          </Button>
        </div>
      </section>
    </div>
  );
}